import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Arrow } from "../components/Layout";

const systemLayers = [
  ["Measure", "Define what is actually observed, how often and which part of the population can enter the sample."],
  ["Model", "Estimate what cannot be measured directly while keeping model error visible."],
  ["Deploy", "Build the sensing, data and operational software required for the model to survive the field."],
  ["Validate", "Compare against reference measurements and separate model, sampling and operational error."],
  ["Calibrate", "Adapt quickly when genetics, barns, cameras or management conditions shift."],
];

const calibrationSteps = ["Change detected", "Check", "Calibrate", "Verify", "Return to operation"];

export default function ApproachPage() {
  return (
    <div className="page page-approach">
      <section className="page-hero page-section">
        <div>
          <p className="eyebrow">04 · Our approach</p>
          <h1>Deployment is part of the model.</h1>
        </div>
        <div className="page-hero-lead">
          <p>We do not treat Birdwatcher as a prediction endpoint. It is a measurement system that has to keep working after conditions change.</p>
          <p>Every simulation simplifies something. Every real deployment reveals something new.</p>
        </div>
      </section>

      <section className="page-section system-loop-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">The operating loop</p>
            <h2>From measurement to calibration, then back again.</h2>
          </div>
          <p>The system is designed as a loop because field conditions do not stay fixed. Validation and recalibration are part of normal operation, not exceptional events.</p>
        </div>
        <div className="system-loop">
          {systemLayers.map(([title, body], index) => (
            <motion.article
              key={title}
              className="loop-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: index * 0.06, duration: 0.42 }}
            >
              <span>0{index + 1}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="page-section calibration-section">
        <div className="calibration-copy">
          <span className="soft-chip">Rapid calibration</span>
          <h2>Biology moves. The system has to follow.</h2>
          <p>New genetics can shift body shape and weight distributions. Barn geometry, camera placement, lighting and management can also change the relationship the model learned.</p>
          <p>We developed an in-house rapid calibration workflow so adaptation does not mean rebuilding the whole system from zero. The public site intentionally does not expose the implementation details.</p>
        </div>
        <div className="calibration-flow" aria-label="Rapid calibration process">
          {calibrationSteps.map((item, index) => (
            <motion.div
              key={item}
              className="calibration-step"
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.07 }}
            >
              <span>{item}</span>
              {index < calibrationSteps.length - 1 && <i>↓</i>}
            </motion.div>
          ))}
        </div>
      </section>

      <section className="page-section error-philosophy">
        <div className="error-philosophy-title">
          <p className="eyebrow">Errors are information</p>
          <h2>We would rather decompose an error than hide it inside an average score.</h2>
        </div>
        <div className="error-principles">
          <article>
            <strong>Model error</strong>
            <p>How wrong is the individual estimate under the conditions it was calibrated for?</p>
          </article>
          <article>
            <strong>Sampling error</strong>
            <p>How much of the flock was actually represented by the observed animals?</p>
          </article>
          <article>
            <strong>Drift</strong>
            <p>Did biology or the environment move away from the distribution the system learned?</p>
          </article>
          <article>
            <strong>Operational error</strong>
            <p>Did hardware, data flow, placement or field conditions change the measurement itself?</p>
          </article>
        </div>
      </section>

      <section className="page-section dark-callout deployment-callout">
        <div>
          <p className="eyebrow">The product is the full loop</p>
          <h2>A strong model is easy to admire. A system that can be recalibrated, monitored and maintained is harder to build.</h2>
        </div>
        <div>
          <p>Our work includes data acquisition, computer vision, statistical modelling, connected systems, monitoring, validation and the software required to operate them.</p>
          <p>Birdwatcher is one project. The same way of thinking applies to other difficult agricultural measurement and automation problems.</p>
          <a className="text-link light-link" href="mailto:info@agroadvisor.hu?subject=Agroadvisor%20development%20project">Discuss a development problem <Arrow /></a>
        </div>
      </section>

      <section className="page-section next-page-card">
        <span>Next · 05</span>
        <div>
          <h2>Two disciplines, one field problem.</h2>
          <p>Meet the team behind Agroadvisor and Birdwatcher.</p>
        </div>
        <Link className="round-link" to="/team" aria-label="Go to team">→</Link>
      </section>
    </div>
  );
}
